/**
 * @file index.tsx
 *
 * @fileoverview Renders a form for saving job alerts inside of a modal.
 */
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';
import { Branding } from '~/jobiqo/components/Branding';
import GdprConsentCheckboxes from '~/jobiqo/components/GDPRConsent/components/Checkboxes';
import GDPRConsentFormWrapper from '~/jobiqo/components/GDPRConsent/components/FormWrapper';
import { GdprCosentContextType } from '~/jobiqo/components/GDPRConsent/hooks/useGdprConsentContext';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { Select } from '~/vcl/atoms/01-forms/05-select';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { CenterForm } from '~/vcl/organisms/01-form/01-center';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';

interface JobAlertFormProps {
  /**
   * Handle submit event for saving a job search.
   */
  onSubmit: (items) => void;
  /**
   * Event when canceling the form.
   */
  onCancel?: () => void;
  /**
   * The title for the job alert.
   */
  jobAlertTitle: string;
  /**
   * GDPR consent context.
   */
  gdprConsentContext: GdprCosentContextType;
}

export const JobAlertForm = ({
  jobAlertTitle,
  onSubmit,
  onCancel,
  gdprConsentContext: { consentsValidation },
}: JobAlertFormProps) => {
  const intl = useIntl();
  const frontendConfig = useFrontendConfig();
  const notifyIntervalValues =
    frontendConfig.JOBS.JOB_ALERT.popup.notifyIntervalValues;
  const frontendTheme = getFrontendTheme(frontendConfig);
  const { EMAIL_VALIDATION, FIELD_REQUIRED } = getCommonTranslations();
  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(
      Yup.object().shape({
        mail: Yup.string().email(EMAIL_VALIDATION).required(FIELD_REQUIRED),
        ...consentsValidation.schema,
      }),
    ),
  });

  const {
    handleSubmit,
    setValue,
    control,
    formState: { isSubmitting, isValid },
  } = methods;

  useEffect(() => {
    Object.keys(consentsValidation.initialValues).forEach((key) =>
      setValue(key as any, consentsValidation.initialValues[key]),
    );
  }, [consentsValidation.initialValues]);

  return (
    <AlignedCenter>
      <Branding maxHeight={frontendTheme.logo.singlePageMaxHeight} />
      <Spacing />
      <Text tag="p" textAlign="center">
        <FormattedMessage
          id="job_alert_intro"
          defaultMessage="Sign up to receive E-Mail job alerts and be the first to hear about new "
        />
      </Text>
      <Text tag="p" textAlign="center" color="primary">
        {jobAlertTitle}
      </Text>
      <Spacing />
      <FormProvider {...methods}>
        <GDPRConsentFormWrapper consentPlacement="JOB_ALERT">
          <CenterForm
            minWidth="0"
            onSubmit={handleSubmit((formData) => onSubmit(formData))}
          >
            <Controller
              name="mail"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Input
                    {...field}
                    id={field.name}
                    error={!!error}
                    type="email"
                    size={80}
                    label={intl.formatMessage({
                      id: 'job_alert_email_field',
                      defaultMessage: 'E-mail',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'job_alert_email_placeholder',
                      defaultMessage: 'Enter an email address',
                    })}
                  />
                  {error ? <InlineError message={error.message} /> : null}
                </>
              )}
            />

            {notifyIntervalValues && (
              <>
                <Controller
                  // @todo check config in jobs.js
                  name="notify_interval"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        name={field.name}
                        id="notify_interval"
                        label={intl.formatMessage({
                          id: 'notify_interval',
                          defaultMessage: 'Notification interval',
                        })}
                        onChange={(item) =>
                          setValue(field.name, item ? item.code : null)
                        }
                        items={notifyIntervalValues}
                      />
                      {error && <InlineError message={error.message} />}
                    </>
                  )}
                />
              </>
            )}
            <GdprConsentCheckboxes />
            <Spacing size="medium" />
            <Button
              disabled={isSubmitting || !isValid}
              block={true}
              type="submit"
              buttonStyle="primary"
              data-test-id="create_job_alert_btn"
            >
              <FormattedMessage
                id="create_job_alert"
                defaultMessage="Create Job Alert"
              />
            </Button>
            <Spacing />
            {onCancel && (
              <Button
                onClick={onCancel}
                buttonStyle="secondary"
                raised={false}
                block={true}
                type="button"
              >
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
            )}
          </CenterForm>
        </GDPRConsentFormWrapper>
      </FormProvider>
    </AlignedCenter>
  );
};
export default JobAlertForm;
